import { memo, useCallback } from 'react';
import { cn, PopoverInfo } from '@divlab/divanui';
import { IconInfoPopup } from '@divlab/divanui/icons';

import { useMeta } from '@Queries/Meta';
import useSearchParams from '@Hooks/useSearchParams';
import useNavigation from '@Navigation/useNavigation';
import Img from '@UI/Img';
import Link from '@Navigation/Link';
import styles from './ReadySolutions.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { Color, ReadySolutionsData } from '@Pages/PageProduct/typings';

export interface OptionCardProps extends HTMLAttributes<HTMLDivElement> {
  currentSolutionId: number;
  readySolutions: ReadySolutionsData;
}

interface SlotTriggerProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  active?: boolean;
}

const SlotTrigger: FC<SlotTriggerProps> = (props) => {
  const { active, title } = props;

  return (
    <div className={styles.name}>
      <Link view='primary'>{title}</Link>
      <IconInfoPopup className={styles.icon} active={active} />
    </div>
  );
};

const ReadySolutions: FC<OptionCardProps> = (props) => {
  const { currentSolutionId, readySolutions, className, ...restProps } = props;
  const { colors, collection } = readySolutions;
  const { navigate } = useNavigation();
  const searchParams = useSearchParams();
  const oldHash = searchParams.get('hash');
  const meta = useMeta();
  const { region } = meta.data;

  const handleClickLink = useCallback(
    (product: Color) => {
      const url = oldHash
        ? `${region.url}${product.link}&hash=${oldHash}`
        : `${region.url}${product.link}`;

      navigate(url, { preventScrollReset: true, replace: true });
    },
    [oldHash, region, navigate],
  );

  return (
    <div {...restProps} className={cn(styles.wrap, className)}>
      <PopoverInfo
        slotTrigger={<SlotTrigger title={collection.title} />}
        preferredPositions={['bottom-right']}
      >
        <div className={styles.popover}>{collection.info}</div>
      </PopoverInfo>

      <div className={styles.list}>
        {colors.map((product) => (
          <Link
            key={product.link}
            onClick={() => handleClickLink(product)}
            to={product.link}
            preventDefault
          >
            <div
              className={cn(styles.container, {
                [styles.selected]: product.shopProductId === currentSolutionId,
              })}
            >
              <Img className={styles.imageWrapper} src={product.image} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default memo(ReadySolutions);
